import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTab, setShowTab] = useState(true);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if user has already seen/dismissed the popup
    const hasSeenPopup = localStorage.getItem('newsletterPopupSeen');
    const hasSubscribed = localStorage.getItem('newsletterSubscribed');
    
    if (!hasSeenPopup && !hasSubscribed) {
      // Show popup after 3 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    } else {
      // Show side tab for users who dismissed or haven't subscribed
      setShowTab(!hasSubscribed);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('newsletterPopupSeen', 'true');
  };

  const handleOpenFromTab = () => {
    setIsOpen(true);
    setShowTab(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!consent) {
      toast({
        title: "Consent Required",
        description: "Please agree to receive marketing emails.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/customers/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          marketingConsent: consent,
          source: 'popup'
        }),
      });

      if (!response.ok) throw new Error('Failed to subscribe');

      const data = await response.json();
      
      setDiscountCode(data.discount_code);
      setShowSuccess(true);
      localStorage.setItem('newsletterSubscribed', 'true');
      
      toast({
        title: "Success! 🎉",
        description: data.discount_message || "You've received your 10% discount code!",
        duration: 5000,
      });
      
      // Hide tab permanently
      setShowTab(false);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(discountCode);
    toast({
      title: "Copied!",
      description: "Discount code copied to clipboard.",
      duration: 2000,
    });
  };

  if (!isOpen && !showTab) return null;

  return (
    <>
      {/* Side Tab */}
      {showTab && !isOpen && (
        <button
          onClick={handleOpenFromTab}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-50 px-4 py-6 text-white font-bold text-lg shadow-lg hover:scale-105 transition-transform"
          style={{
            backgroundColor: '#FF8C19',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            borderRadius: '8px 0 0 8px',
          }}
        >
          🎁 GET 10% OFF
        </button>
      )}

      {/* Popup Modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fadeIn"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-lg shadow-2xl max-w-md w-full relative animate-slideUp"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>

              {!showSuccess ? (
                <>
                  {/* Header */}
                  <div className="text-center pt-8 pb-6 px-6">
                    <div
                      className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
                      style={{ backgroundColor: '#FF8C1920' }}
                    >
                      <span className="text-5xl">🍁</span>
                    </div>
                    <h2 className="text-3xl font-bold mb-2" style={{ color: '#4a2c1a' }}>
                      Welcome to The Maple Daddies!
                    </h2>
                    <p className="text-lg" style={{ color: '#FF8C19' }}>
                      Get 10% Off Your First Order
                    </p>
                    <p className="text-sm mt-2" style={{ color: '#736c64' }}>
                      Sign up for our newsletter and receive an exclusive discount code
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        className="border-2"
                        style={{ borderColor: '#e5d5c0' }}
                      />
                      <Input
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        className="border-2"
                        style={{ borderColor: '#e5d5c0' }}
                      />
                    </div>
                    <Input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="border-2"
                      style={{ borderColor: '#e5d5c0' }}
                    />
                    
                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id="popup-consent"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        required
                        className="mt-1"
                      />
                      <label htmlFor="popup-consent" className="text-xs" style={{ color: '#736c64' }}>
                        I agree to receive promotional emails and newsletters from The Maple Daddies. You can unsubscribe at any time.
                      </label>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full text-white font-semibold py-6 text-lg hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: '#FF8C19' }}
                    >
                      {isSubmitting ? 'Getting Your Code...' : 'Get My 10% Off Code'}
                    </Button>

                    <p className="text-center text-xs" style={{ color: '#736c64' }}>
                      No spam, just pure maple goodness and exclusive offers!
                    </p>
                  </form>
                </>
              ) : (
                <>
                  {/* Success State */}
                  <div className="text-center p-8">
                    <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                      <span className="text-5xl">🎉</span>
                    </div>
                    <h2 className="text-3xl font-bold mb-3" style={{ color: '#4a2c1a' }}>
                      You're In!
                    </h2>
                    <p className="text-lg mb-6" style={{ color: '#736c64' }}>
                      Here's your exclusive 10% discount code:
                    </p>
                    
                    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 rounded-lg p-6 mb-6" style={{ borderColor: '#FF8C19' }}>
                      <p className="text-sm font-semibold mb-2" style={{ color: '#736c64' }}>
                        Your Discount Code:
                      </p>
                      <p className="text-4xl font-bold mb-3" style={{ color: '#FF8C19' }}>
                        {discountCode}
                      </p>
                      <Button
                        onClick={handleCopyCode}
                        variant="outline"
                        className="border-2"
                        style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
                      >
                        Copy Code
                      </Button>
                    </div>

                    <p className="text-sm mb-4" style={{ color: '#736c64' }}>
                      Use this code at checkout to save 10% on your first order!
                    </p>

                    <div className="space-y-3">
                      <Button
                        onClick={() => {
                          handleClose();
                          window.location.href = '/shop';
                        }}
                        className="w-full text-white font-semibold py-4"
                        style={{ backgroundColor: '#FF8C19' }}
                      >
                        Start Shopping
                      </Button>
                      <Button
                        onClick={handleClose}
                        variant="outline"
                        className="w-full"
                      >
                        Continue Browsing
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default NewsletterPopup;
